import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { toast, Toaster } from "sonner";
import { BarChart3, Users, Stethoscope, Pill, CheckCircle2, Clock, XCircle, TrendingUp, Loader2, Syringe, Salad, MessageCircle, Mail, MessageSquare, Printer, Link2, Lock } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, Legend,
  BarChart, Bar, PieChart, Pie, Cell,
} from "recharts";
import { buildWhatsAppBrandedMessage } from "../lib/clinic";
import { openWhatsAppRaw } from "../lib/whatsapp";

const RANGES = [
  { key: 7, label: "Last 7 days" },
  { key: 30, label: "Last 30 days" },
  { key: 90, label: "Last 90 days" },
  { key: 180, label: "Last 6 months" },
];

const PIE_COLORS = ["#db2777", "#f472b6", "#fb7185", "#f97316", "#eab308", "#22c55e", "#0ea5e9", "#6366f1", "#a855f7", "#64748b"];

function StatTile({ icon: Icon, label, value, tone = "pink", testid }) {
  const tones = {
    pink: "bg-pink-50 border-pink-200 text-pink-800",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    sky: "bg-sky-50 border-sky-200 text-sky-800",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    rose: "bg-rose-50 border-rose-200 text-rose-800",
  };
  return (
    <div className={`border rounded-lg p-4 ${tones[tone]}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider font-semibold opacity-80"><Icon size={14} />{label}</div>
      <div className="mt-1 text-2xl font-bold tabular">{value ?? "—"}</div>
    </div>
  );
}

function ChartCard({ title, subtitle, children, testid }) {
  return (
    <div className="border border-slate-200 rounded-lg bg-white p-4" data-testid={testid}>
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="text-sm font-semibold text-slate-800">{title}</div>
          {subtitle && <div className="text-[11px] text-slate-500">{subtitle}</div>}
        </div>
      </div>
      <div className="h-64 w-full min-w-0">{children}</div>
    </div>
  );
}

function EmptyState({ label }) {
  return (
    <div className="h-full flex items-center justify-center text-xs text-slate-400">{label}</div>
  );
}

export default function Analytics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recalls, setRecalls] = useState(null);
  const [dietShare, setDietShare] = useState(null);
  const [menuSource, setMenuSource] = useState(null);
  const [portal, setPortal] = useState(null);
  const [tracker, setTracker] = useState(null);
  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [summary, rec, ds, ms, po, tr] = await Promise.all([
        api.get(`/analytics/summary?days=${days}`),
        api.get(`/analytics/vaccine-recalls?days=${days}`).catch(() => ({ data: null })),
        api.get(`/diet/share-analytics?days=${days}`).catch(() => ({ data: null })),
        api.get(`/diet/menu-source-analytics?weeks=8`).catch(() => ({ data: null })),
        api.get(`/portal/analytics?days=${days}`).catch(() => ({ data: null })),
        api.get(`/portal/tracker?days=${days}`).catch(() => ({ data: null })),
      ]);
      setData(summary.data);
      setRecalls(rec.data);
      setDietShare(ds.data);
      setMenuSource(ms.data);
      setPortal(po.data);
      setTracker(tr.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, [days]);
  useEffect(() => { load(); }, [load]);

  const totals = data?.totals || {};
  const fu = data?.follow_up_compliance || {};
  const visitsSeries = data?.visits_per_day || [];
  const regsSeries = data?.patient_registrations_per_day || [];
  // Merge two series for a combined line chart
  const seriesMerged = visitsSeries.map((v, i) => ({
    date: v.date.slice(5), // MM-DD for compact axis
    Visits: v.count,
    "New patients": regsSeries[i]?.count ?? 0,
  }));

  const dxData = (data?.top_diagnoses || []).slice(0, 10);
  const medData = (data?.top_medicines || []).slice(0, 10);
  const mixData = data?.visit_type_mix || [];
  const complianceData = [
    { name: "Kept", value: fu.kept || 0, color: "#22c55e" },
    { name: "Missed", value: fu.missed || 0, color: "#ef4444" },
    { name: "Pending", value: fu.pending || 0, color: "#f59e0b" },
  ].filter((s) => s.value > 0);

  return (
    <Layout>
      <Toaster position="top-right" richColors />
      <div className="max-w-7xl mx-auto p-4 space-y-5" data-testid="analytics-page">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <BarChart3 className="text-pink-600" size={26} /> Clinic Analytics
            </h1>
            <div className="text-xs text-slate-500 mt-0.5">Visit volume, diagnosis mix, follow-up compliance and prescribing trends.</div>
          </div>
          <div className="inline-flex rounded-md border border-slate-200 overflow-hidden text-xs">
            {RANGES.map((r) => (
              <button
                key={r.key}
                onClick={() => setDays(r.key)}
                className={`px-3 py-1.5 ${days === r.key ? "bg-pink-600 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
                data-testid={`analytics-range-${r.key}`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>

        {/* Stat tiles */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatTile icon={Stethoscope} label="Visits" value={totals.visits} tone="pink" testid="stat-visits" />
          <StatTile icon={Users} label="New patients" value={totals.new_patients} tone="sky" testid="stat-new-patients" />
          <StatTile icon={CheckCircle2} label="Follow-ups kept" value={fu.kept} tone="emerald" testid="stat-fu-kept" />
          <StatTile icon={XCircle} label="Follow-ups missed" value={fu.missed} tone="rose" testid="stat-fu-missed" />
          <StatTile icon={TrendingUp} label="Compliance" value={fu.compliance_pct != null ? `${fu.compliance_pct}%` : "—"} tone="amber" testid="stat-compliance" />
        </div>

        {/* Vaccine recall funnel */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatTile icon={Syringe} label="Recalls sent" value={recalls?.sent ?? 0} tone="pink" testid="stat-recalls-sent" />
          <StatTile icon={Users} label="Unique patients" value={recalls?.unique_patients ?? 0} tone="sky" testid="stat-recalls-patients" />
          <StatTile icon={CheckCircle2} label="Converted" value={recalls?.converted ?? 0} tone="emerald" testid="stat-recalls-converted" />
          <StatTile
            icon={TrendingUp}
            label="Recall CTR"
            value={recalls?.sent ? `${recalls.conversion_pct}%` : "—"}
            tone="amber"
            testid="stat-recalls-ctr"
          />
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="animate-spin" size={16} /> Loading…</div>
        )}

        {/* Charts grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ChartCard title="Visits &amp; New Patients per day" subtitle={`Last ${days} days`} testid="chart-visits-per-day">
            {seriesMerged.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={seriesMerged} margin={{ top: 5, right: 15, left: -20, bottom: 0 }}>
                  <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" />
                  <XAxis dataKey="date" fontSize={10} tick={{ fill: "#64748b" }} />
                  <YAxis fontSize={10} tick={{ fill: "#64748b" }} allowDecimals={false} />
                  <Tooltip wrapperStyle={{ fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="Visits" stroke="#db2777" strokeWidth={2} dot={{ r: 2 }} />
                  <Line type="monotone" dataKey="New patients" stroke="#0ea5e9" strokeWidth={2} dot={{ r: 2 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : <EmptyState label="No visits in this window" />}
          </ChartCard>

          <ChartCard title="Follow-up Compliance" subtitle="Kept within (follow-up + 7d) vs missed" testid="chart-fu-compliance">
            {complianceData.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={complianceData} dataKey="value" nameKey="name" outerRadius={90} label={(e) => `${e.name}: ${e.value}`}>
                    {complianceData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Pie>
                  <Tooltip wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : <EmptyState label="No scheduled follow-ups yet" />}
          </ChartCard>

          <ChartCard title="Top 10 Diagnoses" subtitle="By visit count" testid="chart-top-dx">
            {dxData.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dxData} layout="vertical" margin={{ top: 5, right: 15, left: 10, bottom: 0 }}>
                  <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" fontSize={10} tick={{ fill: "#64748b" }} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" fontSize={10} tick={{ fill: "#334155" }} width={110} />
                  <Tooltip wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="count" fill="#db2777" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <EmptyState label="No diagnoses recorded" />}
          </ChartCard>

          <ChartCard title="Top 10 Medicines Prescribed" subtitle="By count of Rx lines" testid="chart-top-meds">
            {medData.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={medData} layout="vertical" margin={{ top: 5, right: 15, left: 10, bottom: 0 }}>
                  <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" fontSize={10} tick={{ fill: "#64748b" }} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" fontSize={10} tick={{ fill: "#334155" }} width={110} />
                  <Tooltip wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="count" fill="#0ea5e9" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <EmptyState label="No prescriptions in this window" />}
          </ChartCard>

          <ChartCard title="Visit type mix" subtitle="Service mix (revenue proxy)" testid="chart-visit-mix">
            {mixData.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={mixData} dataKey="count" nameKey="type" outerRadius={90} label={(e) => `${e.type}: ${e.count}`}>
                    {mixData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : <EmptyState label="No visits yet" />}
          </ChartCard>

          <ChartCard title="Prescribing snapshot" subtitle="Unique diagnoses vs unique medicines" testid="chart-snapshot">
            <div className="h-full grid grid-cols-2 gap-3">
              <div className="border border-pink-100 bg-pink-50/50 rounded-lg p-4 flex flex-col justify-center items-center">
                <div className="text-[11px] uppercase text-pink-700 font-semibold tracking-wider flex items-center gap-1"><Stethoscope size={12} /> Diagnoses</div>
                <div className="text-4xl font-bold text-pink-900 tabular mt-1">{totals.unique_diagnoses ?? "—"}</div>
                <div className="text-[11px] text-slate-500">unique ICD/DX seen</div>
              </div>
              <div className="border border-sky-100 bg-sky-50/50 rounded-lg p-4 flex flex-col justify-center items-center">
                <div className="text-[11px] uppercase text-sky-700 font-semibold tracking-wider flex items-center gap-1"><Pill size={12} /> Medicines</div>
                <div className="text-4xl font-bold text-sky-900 tabular mt-1">{totals.unique_medicines ?? "—"}</div>
                <div className="text-[11px] text-slate-500">unique brands prescribed</div>
              </div>
            </div>
          </ChartCard>

          <ChartCard
            title="Vaccine recall funnel"
            subtitle={`Sent vs converted — last ${days} days`}
            testid="chart-recall-funnel"
          >
            {recalls?.per_day && recalls.per_day.some((d) => d.sent || d.converted) ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={recalls.per_day.map((d) => ({ date: d.date.slice(5), Sent: d.sent, Converted: d.converted }))}
                  margin={{ top: 5, right: 15, left: -20, bottom: 0 }}
                >
                  <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" />
                  <XAxis dataKey="date" fontSize={10} tick={{ fill: "#64748b" }} />
                  <YAxis fontSize={10} tick={{ fill: "#64748b" }} allowDecimals={false} />
                  <Tooltip wrapperStyle={{ fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="Sent" stroke="#db2777" strokeWidth={2} dot={{ r: 2 }} />
                  <Line type="monotone" dataKey="Converted" stroke="#22c55e" strokeWidth={2} dot={{ r: 2 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : <EmptyState label="No vaccine recalls sent yet — try the Bulk Nudge panel on the Dashboard" />}
          </ChartCard>
        </div>

        {/* ── Diet Share Analytics ────────────────────────── */}
        <DietShareSection dietShare={dietShare} days={days} />

        {/* ── AI vs Curated Menu (weekly) ──────────────── */}
        <MenuSourceSection menuSource={menuSource} />

        {/* ── Patient Portal Analytics ───────────────────── */}
        <PortalSection portal={portal} tracker={tracker} days={days} />
        <PriceCoverageSection />

        <div className="text-[11px] text-slate-400 pt-2">
          <Clock size={11} className="inline mr-1" />
          Generated {data?.generated_at ? new Date(data.generated_at).toLocaleString() : "…"}. Follow-up compliance uses a 7-day grace window after the requested review date.
        </div>
      </div>
    </Layout>
  );
}


// ─── DietShareSection ──────────────────────────────────────────────────
// Compact block showing WhatsApp/Email/SMS/Print tap counts across the
// clinic's generated v2 diet plans + a per-day line chart and a "top plans
// by shares" table. Empty state prompts the doctor to share a plan.
const CHANNEL_META = {
  whatsapp:  { label: "WhatsApp", color: "#22c55e", icon: MessageCircle },
  email:     { label: "Email",    color: "#0ea5e9", icon: Mail },
  sms:       { label: "SMS",      color: "#a855f7", icon: MessageSquare },
  print:     { label: "Print/PDF",color: "#334155", icon: Printer },
  copy_link: { label: "Copy link",color: "#f59e0b", icon: Salad },
};

function DietShareSection({ dietShare, days }) {
  if (!dietShare) return null;
  const totals = dietShare.totals || {};
  const byChannel = dietShare.by_channel || [];
  const channelMap = Object.fromEntries(byChannel.map((r) => [r.channel, r.count]));
  const daySeries = (dietShare.by_day || []).map((d) => ({ date: d.date.slice(5), Shares: d.count }));
  const hasData = totals.total_events > 0;

  return (
    <div className="border border-emerald-200 rounded-lg bg-emerald-50/40 p-4 space-y-4" data-testid="diet-share-section">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Salad className="text-emerald-700" size={18} />
          <div>
            <div className="text-sm font-semibold text-slate-800">Diet Chart Reach</div>
            <div className="text-[11px] text-slate-500">How many generated diet plans made it to patients — last {days} days.</div>
          </div>
        </div>
        {hasData && (
          <div className="text-[11px] text-emerald-800 bg-white border border-emerald-200 rounded px-2 py-1" data-testid="diet-share-last">
            Last share: {totals.last_event_at ? new Date(totals.last_event_at).toLocaleString() : "—"}
          </div>
        )}
      </div>

      {/* Channel tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatTile icon={Salad}          label="Total shares"   value={totals.total_events || 0}    tone="emerald" testid="stat-diet-total" />
        <StatTile icon={MessageCircle}  label="WhatsApp"       value={channelMap.whatsapp || 0}    tone="emerald" testid="stat-diet-wa" />
        <StatTile icon={Mail}           label="Email"          value={channelMap.email || 0}       tone="sky"     testid="stat-diet-email" />
        <StatTile icon={Printer}        label="Print / PDF"    value={channelMap.print || 0}       tone="amber"   testid="stat-diet-print" />
        <StatTile icon={Users}          label="Patients reached" value={totals.unique_patients || 0} tone="pink"  testid="stat-diet-reach" />
      </div>

      {!hasData && (
        <EmptyState label="No diet plans shared yet — generate an Advanced Chart and tap WhatsApp / Email from the linked-patient bar." />
      )}

      {hasData && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ChartCard title="Shares per day" subtitle={`Every tap on WhatsApp / Email / SMS / Print`} testid="chart-diet-shares">
            {daySeries.some((r) => r.Shares > 0) ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={daySeries} margin={{ top: 5, right: 15, left: -20, bottom: 0 }}>
                  <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" />
                  <XAxis dataKey="date" fontSize={10} tick={{ fill: "#64748b" }} />
                  <YAxis fontSize={10} tick={{ fill: "#64748b" }} allowDecimals={false} />
                  <Tooltip wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="Shares" stroke="#059669" strokeWidth={2} dot={{ r: 2 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : <EmptyState label="No shares in this window" />}
          </ChartCard>

          <div className="border border-slate-200 rounded-lg bg-white p-4" data-testid="diet-top-plans">
            <div className="flex items-baseline justify-between mb-3">
              <div>
                <div className="text-sm font-semibold text-slate-800">Top plans by shares</div>
                <div className="text-[11px] text-slate-500">Plans that made it to patients the most.</div>
              </div>
            </div>
            <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
              {(dietShare.top_plans || []).length === 0 ? (
                <div className="text-xs text-slate-400 italic py-6 text-center">No shares yet.</div>
              ) : (
                (dietShare.top_plans || []).map((p) => (
                  <div key={p.plan_id} className="py-2 flex items-center gap-3 text-xs" data-testid={`diet-top-plan-${p.plan_id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-800 truncate">
                        {p.patient_name || <span className="text-slate-400 italic">Unknown</span>}
                        {p.patient_id && <span className="text-[10px] text-slate-400 ml-1">({p.patient_id})</span>}
                      </div>
                      <div className="text-[10px] text-slate-500 truncate">
                        {(p.condition_codes || []).join(" + ") || "General"}
                        <span className="mx-1 text-slate-300">·</span>
                        <span className="text-slate-400">plan {p.plan_id}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 text-[10px] shrink-0">
                      {["whatsapp", "email", "sms", "print"].map((c) => {
                        const n = p[c] || 0;
                        if (!n) return null;
                        const M = CHANNEL_META[c];
                        const Icon = M.icon;
                        return (
                          <span key={c} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-slate-50 border border-slate-200 text-slate-700" title={M.label}>
                            <Icon size={10} style={{ color: M.color }} /> {n}
                          </span>
                        );
                      })}
                      <span className="ml-1 font-bold text-slate-800">{p.total}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Recent tap feed */}
      {hasData && (dietShare.recent || []).length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="diet-share-recent">
          <div className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-1.5"><Clock size={13} className="text-slate-500" /> Recent shares</div>
          <div className="divide-y divide-slate-100 max-h-52 overflow-y-auto">
            {(dietShare.recent || []).map((r) => {
              const M = CHANNEL_META[r.channel] || { label: r.channel, color: "#64748b", icon: Salad };
              const Icon = M.icon;
              return (
                <div key={r.event_id} className="py-1.5 flex items-center gap-2 text-[11px]" data-testid={`diet-share-event-${r.event_id}`}>
                  <Icon size={12} style={{ color: M.color }} />
                  <span className="text-slate-800 font-medium">{M.label}</span>
                  <span className="text-slate-400">→</span>
                  <span className="text-slate-700 truncate">{r.patient_name || r.patient_id || "—"}</span>
                  <span className="text-slate-300">·</span>
                  <span className="text-slate-500 truncate">{(r.condition_codes || []).join(", ") || "General"}</span>
                  <span className="ml-auto text-slate-400">{r.created_at ? new Date(r.created_at).toLocaleString() : ""}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}


// ─── PortalSection ─────────────────────────────────────────────────────
// Compact funnel + trend for patient-portal engagement. Answers: "of the
// WhatsApp Rx links I sent this month, how many did the patient actually
// open — and of those, how many entered their OTP and saw the full Rx?"
function PortalSection({ portal, tracker, days }) {
  if (!portal) return null;
  const f = portal.funnel || {};
  const has = (f.links_created || 0) > 0;
  const daySeries = (portal.by_day || []).map((d) => ({
    date: d.date.slice(5),
    Opened: d.opened || 0,
    Verified: d.verified || 0,
  }));

  return (
    <div className="border border-pink-200 rounded-lg bg-pink-50/40 p-4 space-y-4" data-testid="portal-section">
      <div className="flex items-center gap-2">
        <Link2 className="text-pink-700" size={18} />
        <div>
          <div className="text-sm font-semibold text-slate-800">Patient Portal Reach</div>
          <div className="text-[11px] text-slate-500">How many WhatsApp Rx links the patient actually opened + unlocked — last {days} days.</div>
        </div>
      </div>

      {/* Funnel tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatTile icon={Link2}          label="Links sent"           value={f.links_created || 0}    tone="pink"    testid="stat-portal-sent" />
        <StatTile icon={TrendingUp}     label="Opened (unique)"      value={f.opened_unique || 0}    tone="sky"     testid="stat-portal-opened" />
        <StatTile icon={CheckCircle2}   label="Verified with OTP"    value={f.verified_unique || 0}  tone="emerald" testid="stat-portal-verified" />
        <StatTile icon={XCircle}        label="OTP failed"           value={(portal.by_event || []).find((r) => r.event === "otp_failed")?.count || 0} tone="rose" testid="stat-portal-fail" />
        <StatTile icon={Lock}           label="Verify rate"          value={`${f.verify_rate || 0}%`} tone="amber"  testid="stat-portal-verify-rate" />
      </div>

      {!has && (
        <EmptyState label="No portal links sent yet — hit “Share Rx” from a printed prescription and the patient's engagement will show up here." />
      )}

      {has && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ChartCard title="Opened vs Verified per day" subtitle="Every WhatsApp tap that landed on the portal, and every OTP that succeeded" testid="chart-portal">
            {daySeries.some((r) => r.Opened + r.Verified > 0) ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={daySeries} margin={{ top: 5, right: 15, left: -20, bottom: 0 }}>
                  <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" />
                  <XAxis dataKey="date" fontSize={10} tick={{ fill: "#64748b" }} />
                  <YAxis fontSize={10} tick={{ fill: "#64748b" }} allowDecimals={false} />
                  <Tooltip wrapperStyle={{ fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 10 }} iconSize={8} />
                  <Line type="monotone" dataKey="Opened"   stroke="#0ea5e9" strokeWidth={2} dot={{ r: 2 }} />
                  <Line type="monotone" dataKey="Verified" stroke="#059669" strokeWidth={2} dot={{ r: 2 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : <EmptyState label="No portal traffic in this window" />}
          </ChartCard>

          <div className="border border-slate-200 rounded-lg bg-white p-4" data-testid="portal-recent">
            <div className="flex items-baseline justify-between mb-3">
              <div>
                <div className="text-sm font-semibold text-slate-800">Recently verified</div>
                <div className="text-[11px] text-slate-500">Patients who unlocked and read their Rx.</div>
              </div>
            </div>
            <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
              {(portal.recent_verified || []).length === 0 ? (
                <div className="text-xs text-slate-400 italic py-6 text-center">No verified opens yet.</div>
              ) : (
                (portal.recent_verified || []).map((r) => (
                  <div key={r.event_id} className="py-2 flex items-center gap-2 text-[11px]" data-testid={`portal-verified-${r.event_id}`}>
                    <CheckCircle2 size={12} className="text-emerald-600 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-800 truncate">
                        {r.patient_name || <span className="text-slate-400 italic">Unknown</span>}
                        {r.patient_id && <span className="text-[10px] text-slate-400 ml-1">({r.patient_id})</span>}
                      </div>
                      <div className="text-[10px] text-slate-500 truncate">Link sent by {r.created_by || "—"}</div>
                    </div>
                    <span className="text-[10px] text-slate-400 shrink-0">{r.created_at ? new Date(r.created_at).toLocaleString() : ""}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
      {tracker && (tracker.rows || []).length > 0 && (
        <KitScanTracker tracker={tracker} />
      )}
    </div>
  );
}

// Per-patient care-kit engagement list — "who never opened their link" with a
// one-tap branded WhatsApp nudge for the non-openers.
function KitScanTracker({ tracker }) {
  const rows = tracker.rows || [];
  const remind = (r) => {
    const link = r.portal_url || `${window.location.origin}/portal/${r.token}`;   // MT-12B: server URL carries the tenant locator
    const body =
      `We shared your personal care kit & prescription link, but it looks like it hasn't been opened yet.\n\n` +
      `🔗 Open it here: ${link}\n` +
      `🔑 OTP: the last 4 digits of your mobile number\n\n` +
      `आपली केअर किट व प्रिस्क्रिप्शन लिंक अजून उघडलेली दिसत नाही. वरील लिंक उघडा — OTP म्हणजे तुमच्या मोबाईल क्रमांकाचे शेवटचे ४ अंक.`;
    const msg = buildWhatsAppBrandedMessage({
      lang: "en",
      title: "Your Care Kit is waiting",
      body,
      patientName: r.patient_name || "",
      kind: "education",
    });
    openWhatsAppRaw(r.mobile, msg);
  };

  const fmt = (s) => (s ? new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "");

  return (
    <div className="border border-slate-200 rounded-lg bg-white p-4" data-testid="kit-scan-tracker">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="text-sm font-semibold text-slate-800">Kit Scan Tracker</div>
          <div className="text-[11px] text-slate-500">Every patient a care-kit link was sent to — nudge the ones who never opened it.</div>
        </div>
        {tracker.not_opened > 0 && (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 font-semibold" data-testid="kit-tracker-not-opened-count">
            {tracker.not_opened} not opened
          </span>
        )}
      </div>
      <div className="max-h-80 overflow-y-auto divide-y divide-slate-100">
        {rows.map((r) => (
          <div key={r.patient_id} className="py-2 flex items-center gap-3 text-[11px]" data-testid={`kit-tracker-row-${r.patient_id}`}>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-slate-800 truncate">
                {r.patient_name || <span className="text-slate-400 italic">Unknown</span>}
                <span className="text-[10px] text-slate-400 ml-1">({r.patient_id})</span>
              </div>
              <div className="text-[10px] text-slate-500">
                Sent {fmt(r.last_sent_at)}{r.links > 1 ? ` · ${r.links} links` : ""}{r.expired ? " · link expired" : ""}
              </div>
            </div>
            {r.verified ? (
              <span className="shrink-0 inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-semibold" data-testid={`kit-tracker-status-${r.patient_id}`}>
                <CheckCircle2 size={11} /> Read {fmt(r.last_opened_at)}
              </span>
            ) : r.opened ? (
              <span className="shrink-0 inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-sky-100 text-sky-800 font-semibold" data-testid={`kit-tracker-status-${r.patient_id}`}>
                <TrendingUp size={11} /> Opened {fmt(r.last_opened_at)}
              </span>
            ) : (
              <span className="shrink-0 inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 font-semibold" data-testid={`kit-tracker-status-${r.patient_id}`}>
                <XCircle size={11} /> Not opened
              </span>
            )}
            {!r.opened && r.mobile && !r.expired && (
              <button
                onClick={() => remind(r)}
                className="shrink-0 inline-flex items-center gap-1 text-[10px] px-2.5 py-1 rounded-full border border-emerald-300 bg-white text-emerald-800 hover:bg-emerald-50 font-semibold"
                title={`Send a WhatsApp reminder to ${r.mobile}`}
                data-testid={`kit-tracker-remind-${r.patient_id}`}
              >
                <MessageCircle size={11} /> Remind
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


// ─── PriceCoverageSection ──────────────────────────────────────────────
// Which frequently prescribed medicines have no ₹ price in My Drug List —
// fill the gaps once and cost estimates become complete.
function PriceCoverageSection() {
  const [rep, setRep] = useState(null);
  const [inputs, setInputs] = useState({});
  const [saving, setSaving] = useState("");

  const load = () =>
    api.get("/mydrugs/coverage-report?days=180")
      .then((r) => setRep(r.data))
      .catch((e) => console.debug("[silent]", e));
  useEffect(() => { load(); }, []);
  if (!rep || rep.total_rows === 0) return null;

  const keyOf = (u) => `${u.brand_name}|${u.generic_name}|${u.strength}`;
  const upd = (k, field, v) => setInputs((s) => ({ ...s, [k]: { ...(s[k] || {}), [field]: v } }));

  const save = async (u) => {
    const k = keyOf(u);
    const price = parseFloat((inputs[k]?.price || "").trim());
    if (!price || price <= 0) { toast.error("Enter a valid pack price in ₹"); return; }
    setSaving(k);
    try {
      const r = await api.post("/mydrugs/quick-add", {
        brand_name: u.brand_name,
        generic_name: u.generic_name,
        strength: u.strength,
        price,
        pack: (inputs[k]?.pack || "").trim(),
      });
      toast.success(`${u.brand_name || u.generic_name} priced at ₹${price}`, {
        description: r.data?.now_matches ? "Future prescriptions will now show this cost." : "Saved to My Drug List.",
      });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save the price");
    } finally {
      setSaving("");
    }
  };

  const pct = rep.coverage_pct;
  const pctCls = pct >= 90 ? "text-emerald-700" : pct >= 60 ? "text-amber-700" : "text-rose-700";
  return (
    <div className="border border-slate-200 rounded-lg bg-white p-4" data-testid="price-coverage-section">
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
        <div>
          <div className="text-sm font-semibold text-slate-800">Price Coverage — My Drug List</div>
          <div className="text-[11px] text-slate-500">Prescriptions from the last {rep.days} days · add missing medicines via Import to complete your ₹ estimates.</div>
        </div>
        <div className="text-right">
          <span className={`text-2xl font-extrabold tabular ${pctCls}`} data-testid="price-coverage-pct">{pct}%</span>
          <span className="text-[11px] text-slate-500 ml-1.5">priced ({rep.priced_rows}/{rep.total_rows} prescribed rows)</span>
        </div>
      </div>
      {rep.unmatched.length === 0 ? (
        <div className="text-[12px] text-emerald-700 font-medium" data-testid="price-coverage-complete">
          Every frequently prescribed medicine has a price match — estimates are complete.
        </div>
      ) : (
        <div className="max-h-72 overflow-y-auto border border-slate-100 rounded-md">
          <table className="w-full text-[11.5px]">
            <thead>
              <tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500 sticky top-0">
                <th className="px-3 py-1.5 font-semibold">Medicine without a price</th>
                <th className="px-3 py-1.5 font-semibold">Generic / strength</th>
                <th className="px-3 py-1.5 font-semibold text-right whitespace-nowrap">Times prescribed</th>
                <th className="px-3 py-1.5 font-semibold whitespace-nowrap">Quick price fill</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rep.unmatched.map((u, i) => {
                const k = keyOf(u);
                return (
                <tr key={k} className="hover:bg-slate-50/70" data-testid={`price-gap-row-${i}`}>
                  <td className="px-3 py-1.5 font-medium text-slate-800">{u.brand_name || <span className="italic text-slate-400">(generic only)</span>}</td>
                  <td className="px-3 py-1.5 text-slate-500">{[u.generic_name, u.strength].filter(Boolean).join(" · ") || "—"}</td>
                  <td className="px-3 py-1.5 text-right font-bold text-slate-700 tabular">{u.count}</td>
                  <td className="px-3 py-1.5">
                    <div className="flex items-center gap-1.5">
                      <span className="text-slate-400">₹</span>
                      <input
                        type="number"
                        min="1"
                        step="0.01"
                        value={inputs[k]?.price || ""}
                        onChange={(e) => upd(k, "price", e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && save(u)}
                        placeholder="Pack ₹"
                        className="w-20 h-7 rounded border border-slate-200 px-1.5 text-[11px] tabular focus:outline-none focus:border-pink-400"
                        data-testid={`price-fill-input-${i}`}
                      />
                      <input
                        value={inputs[k]?.pack || ""}
                        onChange={(e) => upd(k, "pack", e.target.value)}
                        placeholder="strip of 10 tablets"
                        className="w-32 h-7 rounded border border-slate-200 px-1.5 text-[11px] focus:outline-none focus:border-pink-400"
                        data-testid={`price-fill-pack-${i}`}
                      />
                      <button
                        onClick={() => save(u)}
                        disabled={saving === k}
                        className="h-7 px-2.5 rounded bg-pink-600 hover:bg-pink-700 disabled:opacity-50 text-white text-[10.5px] font-semibold"
                        data-testid={`price-fill-save-${i}`}
                      >
                        {saving === k ? "…" : "Save"}
                      </button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// ─── MenuSourceSection ─────────────────────────────────────────────────
// Weekly stacked-ish bar of AI-generated vs Curated-fallback vs Hybrid
// diet plans. Early-warning signal for prompt-engineering: if the
// fallback bar climbs relative to AI, Gemini is struggling and it's time
// to look at recent condition combos or the model.
function MenuSourceSection({ menuSource }) {
  if (!menuSource) return null;
  const series = (menuSource.series || []).map((r) => ({
    week: r.week.slice(5),  // MM-DD is enough for an X-tick label
    AI: r.ai || 0,
    Curated: r.fallback || 0,
    Hybrid: r.hybrid || 0,
    fallback_pct: r.fallback_pct || 0,
  }));
  const totals = series.reduce(
    (a, r) => ({ ai: a.ai + r.AI, fb: a.fb + r.Curated, hy: a.hy + r.Hybrid, total: a.total + r.AI + r.Curated + r.Hybrid }),
    { ai: 0, fb: 0, hy: 0, total: 0 },
  );
  const fbPct = totals.total ? Math.round((totals.fb / totals.total) * 100) : 0;
  const hasData = totals.total > 0;

  return (
    <div className="border border-indigo-200 rounded-lg bg-indigo-50/40 p-4 space-y-3" data-testid="menu-source-section">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Salad className="text-indigo-700" size={18} />
          <div>
            <div className="text-sm font-semibold text-slate-800">AI vs Curated menus (last 8 weeks)</div>
            <div className="text-[11px] text-slate-500">Rising Curated bars = Gemini struggled on those condition combos. Great signal for prompt tuning.</div>
          </div>
        </div>
        {hasData && (
          <div className="text-[11px] text-indigo-800 bg-white border border-indigo-200 rounded px-2 py-1" data-testid="menu-source-fb-pct">
            Fallback share overall: <strong>{fbPct}%</strong> ({totals.fb} of {totals.total})
          </div>
        )}
      </div>

      {!hasData ? (
        <EmptyState label="No advanced diet plans generated yet — this chart lights up as soon as you create a plan." />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <ChartCard title="Plans per week" subtitle="Stacked by menu source" testid="chart-menu-source">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={series} margin={{ top: 5, right: 15, left: -20, bottom: 0 }}>
                <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" />
                <XAxis dataKey="week" fontSize={10} tick={{ fill: "#64748b" }} />
                <YAxis fontSize={10} tick={{ fill: "#64748b" }} allowDecimals={false} />
                <Tooltip wrapperStyle={{ fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 10 }} iconSize={8} />
                <Bar dataKey="AI"      stackId="s" fill="#10b981" />
                <Bar dataKey="Curated" stackId="s" fill="#f59e0b" />
                <Bar dataKey="Hybrid"  stackId="s" fill="#6366f1" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
          <ChartCard title="Fallback %" subtitle="How often the curated bank had to fill in" testid="chart-menu-fb-pct">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={series} margin={{ top: 5, right: 15, left: -20, bottom: 0 }}>
                <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" />
                <XAxis dataKey="week" fontSize={10} tick={{ fill: "#64748b" }} />
                <YAxis fontSize={10} tick={{ fill: "#64748b" }} domain={[0, 100]} unit="%" />
                <Tooltip wrapperStyle={{ fontSize: 12 }} formatter={(v) => `${v}%`} />
                <Line type="monotone" dataKey="fallback_pct" stroke="#d97706" strokeWidth={2} dot={{ r: 2 }} name="Fallback %" />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
          <div className="border border-slate-200 rounded-lg bg-white p-4" data-testid="menu-source-totals">
            <div className="text-sm font-semibold text-slate-800 mb-2">Totals</div>
            <div className="space-y-1 text-xs">
              <MSTile color="#10b981" label="AI (Gemini)"   count={totals.ai}  total={totals.total} />
              <MSTile color="#f59e0b" label="Curated fallback" count={totals.fb} total={totals.total} />
              <MSTile color="#6366f1" label="Hybrid"        count={totals.hy}  total={totals.total} />
            </div>
            {fbPct >= 40 && (
              <div className="mt-3 text-[10px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                Heads up — fallback is firing on <strong>{fbPct}%</strong> of plans. Consider revisiting the diet prompt or Gemini model choice.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function MSTile({ color, label, count, total }) {
  const pct = total ? Math.round((count / total) * 100) : 0;
  return (
    <div className="flex items-center gap-2">
      <span className="inline-block h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: color }} />
      <span className="text-slate-700 flex-1">{label}</span>
      <span className="text-slate-800 font-semibold tabular">{count}</span>
      <span className="text-[10px] text-slate-400 tabular w-10 text-right">{pct}%</span>
    </div>
  );
}

